import { Routes } from "@angular/router";
import { LayoutComponent } from "./core/layout/layout.component";
import { ProxyComponent } from "./core/components/proxy/proxy.component";
import { APP_ROUTES } from "./core/constants/routes";
import { authGuard } from "./core/guards/auth.guard";
import { permissionsGuard } from "./core/guards/permissions.guard";
import { UserManagementComponent } from "./features/main/pages/user-managment/user-management.component";
import { UserRequestsComponent } from "./features/main/pages/user-managment/user-requests/user-requests.component"; // Import
import { ManageAssessmentComponent } from './features/main/pages/manage-assessment/manage-assessment.component';
import { AssessmentDetailsComponent } from './features/main/pages/manage-assessment/assessment-details/assessment-details.component';
import { redirectIfLoggedInGuard } from './core/guards/redirect.guard';
import { UserListComponent } from "./features/main/pages/user-managment/user-list/user-list.component"; // Import
import { AuditLogsComponent } from "./features/main/pages/audit-logs/audit-logs.component";
import { PermissionSettingsComponent } from "./features/main/pages/permission-settings/permission-settings.component";
import { PermissionsComponent } from "./features/main/pages/permission-settings/permissions/permissions.component";
import { PermissionGroupsComponent } from "./features/main/pages/permission-settings/permission-groups/permission-groups.component";
import { ConfigurationsComponent } from "./features/main/pages/configurations/configurations.component";
import { CountryComponent } from "./features/main/pages/configurations/country/country.component";
import { TypeOfChangesComponent } from "./features/main/pages/configurations/type-of-changes/type-of-changes.component";
import { ProductFamilyComponent } from "./features/main/pages/configurations/product-family/product-family.component";

export const routes: Routes = [
  // --- Authentication Routes ---
  {
    path: APP_ROUTES.LOGIN.substring(1),
  canActivate: [redirectIfLoggedInGuard],
    loadComponent: () =>
      import("./features/auth/components/login/login.component").then(
        (m) => m.LoginComponent
      ),
  },
  {
    path: "signup",
    loadComponent: () =>
      import("./features/auth/components/signup/signup.component").then(
        (m) => m.SignupComponent
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import(
        "./features/auth/components/reset-password/reset-password.component"
      ).then((m) => m.ResetPasswordComponent),
  },

  {
    path: 'set-password',
    loadComponent: () =>
      import(
        "./features/auth/components/set-password/set-password.component"
      ).then((m) => m.SetPasswordComponent),
  },

  // --- Utility/Proxy Route ---
  { path: APP_ROUTES.PROXY.substring(1), component: ProxyComponent },

  // --- Callback Route ---
  {
    path: APP_ROUTES.CALLBACK.substring(1),
    loadComponent: () => import('./core/components/callback/callback.component').then(m => m.CallbackComponent)
  },

  // --- Main Application Routes (Protected Section) ---
  {
    path: "",
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: "", redirectTo: APP_ROUTES.HOME.substring(1), pathMatch: "full" },

      {
        path: APP_ROUTES.HOME.substring(1),
        loadComponent: () =>
          import("./features/main/pages/home/home.component").then(
            (m) => m.HomeComponent
          ),
      },
  // Legacy direct routes redirect to unified /home while preserving login flow
  { path: APP_ROUTES.LOCAL_RA_HOME.substring(1), redirectTo: APP_ROUTES.HOME.substring(1), pathMatch: 'full' },
  { path: APP_ROUTES.GLOBAL_RA_HOME.substring(1), redirectTo: APP_ROUTES.HOME.substring(1), pathMatch: 'full' },
      {
        path: 'user-management',
        component: UserManagementComponent,
        canActivate: [permissionsGuard],
        data: { permissions: ['View User','Manage User'], match: 'any', message: 'User Management access is restricted.' },
        children: [
          { path: '', redirectTo: 'requests', pathMatch: 'full' },
          { path: 'requests', component: UserRequestsComponent },
          { path: 'users', component: UserListComponent },
        ]
      },
      {
        path: "audit-logs",
        component: AuditLogsComponent,
      },
      {
        path: "configuration",
        component: ConfigurationsComponent,
        children: [
          { path: "", redirectTo: "permissions", pathMatch: "full" }, // Default child route
          { path: "country", component: CountryComponent },
          { path: "type-of-change", component: TypeOfChangesComponent },
          { path: "product-family", component: ProductFamilyComponent },
        ],
      },
      {
        path: "permission-Settings",
        component: PermissionSettingsComponent,
        children: [
          { path: "", redirectTo: "permissions", pathMatch: "full" }, // Default child route
          { path: "permissions", component: PermissionsComponent },
          { path: "permission-groups", component: PermissionGroupsComponent },
        ],
      },
      {
        path: 'manage-assessment',
        component: ManageAssessmentComponent, // This is now the parent component
        canActivate: [permissionsGuard],
        data: { permissions: ['Manage RCA','View RCA & Manage Response'], match: 'any', message: 'You cannot access Manage Assessment.' },
      },
      {
        path: 'manage-assessment/:id', // Parameterized route for assessment ID
        component: AssessmentDetailsComponent,
        canActivate: [permissionsGuard],
        data: { permissions: ['Manage RCA','View RCA & Manage Response'], match: 'any', message: 'You cannot access Manage Assessment.' },
      },
      {
        path: APP_ROUTES.SOFTWARE_MANAGER.substring(1),
        canActivate: [permissionsGuard],
        data: { permissions: ['View Configuration','Manage Configuration'], match: 'any' },
        loadComponent: () =>
          import(
            "./features/main/pages/software-management/software-management.component"
          ).then((m) => m.SoftwareManagementComponent),
      },
      {
        path: "profile-settings",
        loadComponent: () =>
          import("./features/main/pages/profile-settings/profile-settings.component").then(m => m.ProfileSettingsComponent),
      },
      {
        path: "user-details/:id",
        loadComponent: () =>
          import("./features/main/pages/user-details/user-details.component").then(m => m.UserDetailsComponent),
      },
      {
        path: 'home/top-performing-countries',
        loadComponent: () => import('./features/main/pages/home/top-performing-countries/top-performing-countries.component').then(m => m.TopPerformingCountriesComponent)
      },
      {
        path: 'home/pending-assessments-by-country',
        loadComponent: () => import('./features/main/pages/home/pending-assessments-by-country/pending-assessments-by-country.component').then(m => m.PendingAssessmentsByCountryComponent)
      },
    ],
  },
  { path: "**", redirectTo: APP_ROUTES.LOGIN.substring(1) },
];